import { computed } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia'
import { FLOW_STEP_STATUS } from '~/constants/FlowStepStatus';
import { FLOW_STATUS } from '~/constants/CheckoutFlow';
import { ERROR_CODE } from '~/constants/ErrorCodes';
import { useFlowStore } from '~/stores/useFlowStore';
import { useFlowValidationStore } from '~/stores/useFlowValidationStore';

const SUBSCRIPTION_STEP_BLOCKING_CODES = new Set([
  ERROR_CODE.QUARANTINE,
  ERROR_CODE.NEGATIVE_BALANCE,
  ERROR_CODE.MULTIPLE_SUBSCRIPTION,
  ERROR_CODE.MULTIPLE_BINDING,
  ERROR_CODE.MULTIPLE_BINDING_WITH_FAD,
  ERROR_CODE.PLAY_EMAIL_IN_USE,
  ERROR_CODE.NUMBER_IN_USE
])

function isSubscriptionStepBlockingNotification (item) {
  if (!item?.notificationCode) {
    return false
  }
  if (item.blockingCheckout === true) {
    return true
  }
  return SUBSCRIPTION_STEP_BLOCKING_CODES.has(item.notificationCode)
}

function shouldReplaceNotification (existing, item) {
  if (!existing) {
    return true
  }
  return !existing.userFriendlyMessage && Boolean(item.userFriendlyMessage)
}

function mergeBlockingNotification (byCode, item) {
  if (!isSubscriptionStepBlockingNotification(item)) {
    return
  }
  const code = item.notificationCode
  if (shouldReplaceNotification(byCode.get(code), item)) {
    byCode.set(code, item)
  }
}

function resolveBlockingNotificationsFromResponse (res) {
  const byCode = new Map()
  const fields = ['signupBlockingNotifications', 'errors', 'actionErrors']
  fields.forEach((field) => {
    const list = res[field]
    if (!Array.isArray(list)) {
      return
    }
    list.forEach((item) => {
      mergeBlockingNotification(byCode, item)
    })
  })
  return Array.from(byCode.values())
}

export default function useFlow(app, state) {
  const flowStore = useFlowStore()
  const flowValidationStore = useFlowValidationStore()
  const {
    preValidateInProgress,
    validateInProgress,
    validationResponse,
    validationStatus,
    preValidationStatus,
    signupBlockingNotifications,
    hasSignupBlockingNotification,
    signupBlockingNotificationsValidate,
    hasSignupBlockingNotificationValidate,
    orderValidateFailed,
    orderValidateErrors
  } = storeToRefs(flowValidationStore)


  const { panels, flowHandler } = state
  let waitTime = false; // ensure it doesnt skip a step. prevent double clicks. or if script execution hangs a moment etc.

  const initializeFlow = (appContext) => {
    flowStore.reset()
    flowHandler.initialize(appContext)
    preValidate()
    flowStore.setStatus(FLOW_STATUS.IN_PROGRESS)
    orderValidateFailed.value = false
    orderValidateErrors.value = []
    panels.forEach(() => {
      flowStore.addStep({
        status: FLOW_STEP_STATUS.INCOMPLETE
      })
    })
  }

  const preValidate = () => {
    preValidateInProgress.value = true
    return flowHandler.preValidate().then((res) => {
      preValidationStatus.value = res.actionStatus
      hasSignupBlockingNotification.value = res.hasSignupBlockingNotification
      signupBlockingNotifications.value = res.signupBlockingNotifications
      preValidateInProgress.value = false
    })
  }

  const hasBlockingSignupValidation = (res, blockingNotifications) => {
    if (res.hasSignupBlockingNotification) {
      return true
    }
    return blockingNotifications.length > 0
  }

  const setSignupValidationResult = (res) => {
    validationResponse.value = res
    validationStatus.value = res.actionStatus
    const blockingNotifications = resolveBlockingNotificationsFromResponse(res)
    signupBlockingNotificationsValidate.value = blockingNotifications
    hasSignupBlockingNotificationValidate.value = hasBlockingSignupValidation(res, blockingNotifications)
    if (hasSignupBlockingNotificationValidate.value) {
      flowStore.setStatus(FLOW_STATUS.ERROR)
    }
  }

  const validate = (subscriptionSignUpDataNew) => {
    validateInProgress.value = true
    hasSignupBlockingNotificationValidate.value = null
    signupBlockingNotificationsValidate.value = null
    return flowHandler.validate(subscriptionSignUpDataNew).then((res) => {
      setSignupValidationResult(res)
      validateInProgress.value = false
    })
  }

  const resetSignupValidation = () => {
    validateInProgress.value = false
    hasSignupBlockingNotificationValidate.value = null
    signupBlockingNotificationsValidate.value = null
    validationResponse.value = null
    validationStatus.value = null
    flowStore.setStatus(FLOW_STATUS.IN_PROGRESS)
  }

  const nextStep = async (index) => {
    orderValidateFailed.value = false
    orderValidateErrors.value = []
    const finalized = await flowHandler.finalizeStep(panels[index].stepType, state, app)
    if (finalized === false) {
      orderValidateFailed.value = true
      flowStore.setStatus(FLOW_STATUS.ERROR)
      flowStore.setStepStatus(index, FLOW_STEP_STATUS.ERROR)
      return
    }
    if (waitTime) { return; }
    waitTime = true
    setTimeout(() => {
      if (flowStore.getCurrentStepStatus !== FLOW_STEP_STATUS.ERROR) {
        flowStore.setStepStatus(index, FLOW_STEP_STATUS.COMPLETE)
        flowStore.increaseCurrentStep()
        flowStore.updatePanelModel()
        $nuxt.$emit('flowStepNext', index)
      }(waitTime = false)
    }, 500)
  }

  /**
   * edit step in the checkout
   * @param {number} i
   */
  const editStep = (index) => {
    if (index !== -1) {
      orderValidateFailed.value = false
      orderValidateErrors.value = []
      resetSignupValidation()
      flowStore.setStepStatus(index, FLOW_STEP_STATUS.EDITING)
      flowStore.setCurrentStep(index)
      flowStore.updatePanelModel()
      $nuxt.$emit('flowStepEdit', index)
      flowStore.steps.forEach((_step, i) => {
        if (i > flowStore.currentStep) {
          flowStore.setStepStatus(i, FLOW_STEP_STATUS.EDITING)
        }
      })
    }
  }

  // to stop default panel toggle behavior on click
  const valueComparator = (_a, b) => {
    return b === flowStore.getCurrentStep.value
  }

  const next = async (index) => {
    if (isStepValidationRequired(index)) {
      flowStore.setStepStatus(index, FLOW_STEP_STATUS.FORM_SUBMITTED)
    } else {
      await nextStep(index)
    }
  }

  const isStepValidationRequired = (index) => {
    return panels[index].validationComponent !== undefined
  }

  const performStepFailure = (index) => {
    flowStore.setStepStatus(index, FLOW_STEP_STATUS.ERROR)
  }

  const performStepSuccess = () => {
    flowStore.setStepStatus(flowStore.currentStep, FLOW_STEP_STATUS.FORM_SUBMITTED)
  }

  const isComplete = (index) => {
    return flowStore.getStepStatus(index) === FLOW_STEP_STATUS.COMPLETE
  }

  const currentStepType = computed(() => panels[flowStore.currentStep].stepType)
  const getCurrentStepStatus = computed(() => flowStore.getCurrentStepStatus)

  return {
    validationResponse,
    currentStepType,
    getCurrentStepStatus,
    setFlowStepStatus: flowStore.setStepStatus,
    flowStatus: flowStore.getFlowStatus,
    validateInProgress,
    signupBlockingNotificationsValidate,
    hasSignupBlockingNotificationValidate,
    orderValidateFailed,
    orderValidateErrors,
    preValidateInProgress,
    hasSignupBlockingNotification,
    signupBlockingNotifications,
    validationStatus,
    preValidationStatus,
    preValidate,
    validate,
    resetSignupValidation,
    initializeFlow,
    isComplete,
    valueComparator,
    panelModel: flowStore.getPanelModel,
    currentStep: flowStore.getCurrentStep,
    steps: flowStore.getSteps,
    editStep,
    nextStep,
    waitTime,
    performStepFailure,
    performStepSuccess,
    next,
    panels
  }
}
